import { useEffect, useState } from "react";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import type { DownloadOfferBlock } from "@containers/Hermes/types";

/** Totals for one offer, resolved by /downloads/count. */
export type CountedOffer = {
  available: number;
  maxRows: number;
  creditsPerRow: number;
};

// Kept for the session, keyed by spec_token, so reopening a card neither pays for another
// count query nor shows a different number from the one the action bar already read.
export const countCache = new Map<string, CountedOffer>();

/** The offer's count when it already carries one, else what the session has read, else null. */
export function knownTotal(block: DownloadOfferBlock | undefined): number | null {
  if (!block) return null;
  if (!block.count_pending && block.record_count !== null && block.record_count !== undefined) return block.record_count;
  return countCache.get(block.spec_token)?.available ?? block.matched_records ?? null;
}

/**
 * How many records an offer's result has. New offers carry it from the moment the answer
 * arrives; an older one (or one whose count timed out) is counted here — once, when its
 * action bar is shown — so the number can be displayed, and is then reused by its card.
 */
export default function useOfferTotal(block: DownloadOfferBlock | undefined): number | null {
  const [total, setTotal] = useState<number | null>(() => knownTotal(block));

  useEffect(() => {
    if (!block?.spec_token) return;
    const known = knownTotal(block);
    if (known !== null) {
      setTotal(known);
      return;
    }
    let cancelled = false;
    axios
      .post(getApiUrl("downloads/count"), { spec_token: block.spec_token })
      .then(({ data }) => {
        const payload = data?.data ?? {};
        const counted = { available: payload.deliverable_records ?? 0, maxRows: payload.max_rows ?? block.max_rows ?? 0, creditsPerRow: payload.credits_per_row ?? 1 };
        countCache.set(block.spec_token, counted);
        if (!cancelled) setTotal(counted.available);
      })
      // The number is a nicety here; without it the line just reads "all records".
      .catch((err) => {
        if (!err?.response?.data?.message) Sentry.captureException(err);
      });
    return () => {
      cancelled = true;
    };
  }, [block]);

  return total;
}
